import { alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';

const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

function sys(theme: Theme) {
  return theme.palette.custom.OV.sys;
}

export function cardSx(theme: Theme) {
  return {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    maxHeight: 'var(--reason-popover-max-height, min(70vh, 32.5rem))',
    overflow: 'hidden',
    borderRadius: '0.8125rem',
    backgroundColor: sys(theme).surfaceContainerLowest,
    border: `0.0625rem solid ${sys(theme).outlineVariant}`,
    boxShadow:
      '0 0.0625rem 0.125rem rgba(0,0,0,.12), 0 1.125rem 2.75rem -0.625rem rgba(0,0,0,.42)',
  };
}

export function headerSx(theme: Theme) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.875rem',
    minHeight: '1.375rem',
    minWidth: 0,
    padding: '0.875rem 1.0625rem 0',
    fontFamily: MONO,
    fontSize: '0.65625rem',
    fontWeight: 500,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    color: sys(theme).onSurfaceVariant,
  };
}

export const headerValueSx = {
  letterSpacing: 0,
  textTransform: 'none',
  flexShrink: 0,
} as const;

export function reasoningLabelSx(theme: Theme) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.6875rem 1.0625rem 0',
    fontFamily: MONO,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '.13em',
    textTransform: 'uppercase',
    color: sys(theme).primary,
    '& svg': { width: '0.75rem', height: '0.75rem' },
  };
}

export function reasoningBodySx(theme: Theme) {
  return {
    margin: '0.4375rem 1.0625rem 0',
    fontSize: '0.84375rem',
    lineHeight: 1.6,
    color: sys(theme).onSurface,
    '& p': { margin: '0 0 0.5625rem' },
    '& p:last-child': { margin: 0 },
  };
}

export function sourceHeadingSx(theme: Theme) {
  return {
    margin: '0.875rem 1.0625rem 0',
    paddingTop: '0.625rem',
    fontFamily: MONO,
    fontSize: '0.59375rem',
    fontWeight: 500,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    color: sys(theme).onSurfaceVariant,
    opacity: 0.8,
    borderTop: `0.0625rem solid ${alpha(sys(theme).outlineVariant, 0.7)}`,
  };
}

export function referenceRowSx(theme: Theme) {
  return {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0,1fr) 0.75rem',
    alignItems: 'center',
    gap: '0.625rem',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    padding: '0.3125rem 0.5rem',
    borderRadius: '0.4375rem',
    color: 'inherit',
    font: 'inherit',
    '&:hover, &:focus-visible': {
      backgroundColor: sys(theme).surfaceContainerHigh,
      outline: 'none',
    },
    '&:hover [data-citation-id]': { color: sys(theme).primary },
    '&:hover [data-citation-subject]': { color: sys(theme).onBackground },
    '& svg': {
      width: '0.75rem',
      height: '0.75rem',
      color: sys(theme).onSurfaceVariant,
      opacity: 0.45,
    },
    '&:hover svg': { opacity: 1, color: sys(theme).primary },
  };
}

export function referenceIdSx(theme: Theme) {
  return {
    fontFamily: MONO,
    fontSize: '0.6875rem',
    color: sys(theme).onSurfaceVariant,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  };
}

export function referenceSubjectSx(theme: Theme) {
  return {
    minWidth: 0,
    fontSize: '0.78125rem',
    lineHeight: 1.45,
    color: sys(theme).onSurface,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function emptySourceSx(theme: Theme) {
  return {
    padding: '0.125rem 0.875rem 0.75rem',
    fontSize: '0.75rem',
    color: sys(theme).onSurfaceVariant,
    backgroundColor: sys(theme).surfaceContainerLow,
  };
}

export const triggerWrapSx = { mt: 'auto', pt: '0.875rem' } as const;

export const scrollBodySx = {
  overflowY: 'auto',
  minHeight: 0,
  flex: 1,
  paddingBottom: '0.25rem',
} as const;

export const referencesListSx = { padding: '0.1875rem 0.5625rem 0.6875rem' } as const;

export function triggerButtonSx(theme: Theme) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: 0,
    background: 'none',
    border: 0,
    font: 'inherit',
    fontSize: '0.75rem',
    cursor: 'pointer',
    color: sys(theme).onSurfaceVariant,
    transition: 'color .12s ease',
    '&:hover, &[aria-expanded="true"]': { color: sys(theme).primary },
    '&:focus-visible': {
      outline: `0.125rem solid ${sys(theme).primary}`,
      outlineOffset: '0.1875rem',
      borderRadius: '0.3125rem',
    },
    '& svg': { width: '0.8125rem', height: '0.8125rem' },
  };
}
